<template>
  <div class="parental-control-dashboard">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h3 class="mb-0">Родительская панель</h3>
                <p class="text-muted mb-0">
                  Добро пожаловать, {{ parentInfo.name }}!
                  Отслеживайте прогресс {{ parentInfo.students_count }} студент{{ getStudentWordEnding(parentInfo.students_count) }}
                </p>
              </div>
              <div>
                <b-button
                  variant="outline-primary"
                  @click="refreshData"
                  :disabled="isLoading"
                >
                  <feather-icon icon="RefreshCwIcon" :class="{ 'rotate': isLoading }" />
                  Обновить
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Загрузка...</span>
      </div>
      <p class="mt-3 text-muted">Загрузка данных о студентах...</p>
    </div>

    <!-- Students Dashboard -->
    <div v-else-if="students.length > 0" class="students-dashboard">
      <!-- Students Navigation -->
      <div class="row mb-4" v-if="students.length > 1">
        <div class="col-12">
          <b-nav pills class="nav-justified">
            <b-nav-item
              v-for="student in students"
              :key="student.student_info.id"
              :active="activeStudentId === student.student_info.id"
              @click="selectStudent(student.student_info.id)"
            >
              {{ student.student_info.name }}
            </b-nav-item>
          </b-nav>
        </div>
      </div>

      <!-- Active Student Dashboard -->
      <div v-if="activeStudent" class="student-dashboard">
        <StudentOverview
          :student="activeStudent"
          @view-details="viewStudentDetails"
        />
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-5">
      <feather-icon icon="UsersIcon" size="64" class="text-muted mb-3" />
      <h4 class="text-muted">Нет студентов для мониторинга</h4>
      <p class="text-muted">
        Обратитесь к администратору для привязки студентов к вашему аккаунту
      </p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="alert alert-danger">
      <div class="d-flex align-items-center">
        <feather-icon icon="AlertCircleIcon" size="20" class="mr-2" />
        <div>
          <strong>Ошибка загрузки данных:</strong> {{ error }}
        </div>
      </div>
    </div>

    <!-- Student Details Modal -->
    <StudentDetailsModal
      v-model="showDetailsModal"
      :student-id="selectedStudentId"
    />
  </div>
</template>

<script>
import {
  BButton,
  BNav,
  BNavItem,
  BCard,
  BCardBody,
  BAlert
} from 'bootstrap-vue'
import StudentOverview from './components/StudentOverview.vue'
import StudentDetailsModal from './components/StudentDetailsModal.vue'
import ParentalControlAPI from '@/api/parentalControl'

export default {
  name: 'ParentalControlDashboard',
  components: {
    BButton,
    BNav,
    BNavItem,
    BCard,
    BCardBody,
    BAlert,
    StudentOverview,
    StudentDetailsModal
  },
  data() {
    return {
      isLoading: false,
      error: null,
      parentInfo: {
        name: '',
        email: '',
        students_count: 0
      },
      students: [],
      activeStudentId: null,
      selectedStudentId: null,
      showDetailsModal: false
    }
  },
  computed: {
    activeStudent() {
      return this.students.find(student =>
        student.student_info.id === this.activeStudentId
      )
    }
  },
  methods: {
    async loadDashboardData() {
      this.isLoading = true
      this.error = null

      try {
        const response = await ParentalControlAPI.getParentDashboard()
        const { parent_info, students } = response.data

        this.parentInfo = parent_info
        this.students = students

        // Auto-select first student
        if (students.length > 0) {
          this.activeStudentId = students[0].student_info.id
        }

      } catch (error) {
        this.error = 'Не удалось загрузить данные. Проверьте ваши права доступа.'
        console.error('Dashboard loading error:', error)
      } finally {
        this.isLoading = false
      }
    },
    selectStudent(studentId) {
      this.activeStudentId = studentId
    },
    viewStudentDetails(studentId) {
      this.selectedStudentId = studentId
      this.showDetailsModal = true
    },
    async refreshData() {
      await this.loadDashboardData()
      this.$bvToast.toast('Данные обновлены', {
        title: 'Обновление',
        variant: 'success',
        solid: true,
        autoHideDelay: 2000
      })
    },
    getStudentWordEnding(count) {
      if (count === 1) return 'а'
      if (count >= 2 && count <= 4) return 'ов'
      return 'ов'
    }
  },
  async created() {
    await this.loadDashboardData()
  }
}
</script>

<style lang="scss" scoped>
.parental-control-dashboard {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 20px;
}

.card {
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border-radius: 0.5rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.rotate {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.nav-pills {
  .nav-link {
    border-radius: 0.5rem;
    margin: 0 0.25rem;
    color: #6c757d;
    font-weight: 500;

    &.active {
      background-color: #7367f0;
      border-color: #7367f0;
    }

    &:hover:not(.active) {
      background-color: rgba(115, 103, 240, 0.1);
      color: #7367f0;
    }
  }
}

.alert {
  border-radius: 0.5rem;
  border: none;
}

.dark-layout {
  .parental-control-dashboard {
    background-color: #1e1e2e;
  }

  .card {
    background-color: #283046;
    color: #d0d2d6;
  }

  .text-muted {
    color: #676d7d !important;
  }

  .nav-pills .nav-link {
    color: #b4b7bd;

    &:hover:not(.active) {
      background-color: rgba(115, 103, 240, 0.2);
      color: #7367f0;
    }
  }
}

@media (max-width: 768px) {
  .parental-control-dashboard {
    padding: 10px;
  }

  .nav-pills {
    flex-direction: column;

    .nav-link {
      margin: 0.25rem 0;
    }
  }
}
</style>